import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';

const transformations = [
  {
    before: "Reps update CRM when they feel like it",
    after: "Meetings auto-captured, CRM updates itself from every interaction",
    label: "Data Accuracy",
  },
  {
    before: "Forecast is a guess in a spreadsheet",
    after: "Evidence-backed forecast with AI-scored deal confidence and backtesting",
    label: "Forecast Quality",
  },
  {
    before: "Rep leaves → pipeline goes dark",
    after: "Full deal context, meeting history, and relationships stay. Business continues.",
    label: "Business Continuity",
  },
  {
    before: "Single-threaded & fake deals slip through",
    after: "MEDDIC scoring, buyer engagement alerts, and going-dark detection flag risk early",
    label: "Deal Intelligence",
  },
  {
    before: "Coaching is subjective and infrequent",
    after: "AI coaching from call recordings with evidence-based rep benchmarking",
    label: "Rep Development",
  },
  {
    before: "Competitive intel lives in someone's head",
    after: "Auto-generated battlecards from win/loss data and call mentions",
    label: "Competitive Edge",
  },
];

export const ProblemSection = () => {
  return (
    <section className="w-full py-20 md:py-28 bg-background border-b border-foreground/20">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="max-w-4xl mb-14"
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">What changes</p>
          <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-5">
            Your forecast cannot be stronger than its evidence.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Reps lose hours every week to CRM admin, and pipelines slip because what actually happened in calls and emails never reaches the deal. Here is what changes with NoQ5.
          </p>
        </motion.div>

        <div className="border-t border-foreground/30 max-w-6xl">
          {transformations.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-[180px_1fr_32px_1fr] gap-3 md:gap-6 items-start md:items-center py-6 border-b border-foreground/20"
            >
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                {item.label}
              </div>
              <p className="text-sm text-muted-foreground">{item.before}</p>
              <Minus className="hidden md:block w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">{item.after}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/crm-agent" className="inline-flex items-center gap-2 text-primary hover:underline font-semibold">
            See how the NoQ5 CRM agent solves this <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
